/**
 * useKeyboardShortcuts — global keyboard shortcut handler.
 *
 * Only fires when the user is NOT focused on an input, textarea, or
 * contenteditable element (so typing is never intercepted).
 *
 * Returns `openHelp` so callers can imperatively open the help modal.
 *
 * Exchange-specific shortcuts (L/S/M/K/T/C) emit CustomEvents on
 * window so SwapBox can subscribe without prop-drilling.
 */
import { useEffect, useCallback } from "react";

export const KB_EVENT = "moneyx:kb";

function isTyping(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || (el as HTMLElement).isContentEditable;
}

function emit(action: string) {
  window.dispatchEvent(new CustomEvent(KB_EVENT, { detail: { action } }));
}

type Options = {
  onOpenHelp: () => void;
};

export function useKeyboardShortcuts({ onOpenHelp }: Options) {
  const handler = useCallback(
    (e: KeyboardEvent) => {
      // Never intercept typed characters
      if (isTyping()) return;
      // Never intercept modifier combos except Ctrl+Enter
      if (e.altKey || e.metaKey) return;

      const key = e.key;

      // Global
      if (key === "?" || key === "/") {
        e.preventDefault();
        onOpenHelp();
        return;
      }

      // Exchange shortcuts — emit event so SwapBox (and any listener) can handle
      if (e.ctrlKey && key === "Enter") {
        emit("confirm");
        return;
      }
      if (e.ctrlKey) return; // skip other ctrl combos

      switch (key.toLowerCase()) {
        case "l": emit("long"); break;
        case "s": emit("short"); break;
        case "m": emit("market"); break;
        case "k": emit("limit"); break;
        case "t": emit("trigger"); break;
        case "c": emit("focus-collateral"); break;
        case "+": emit("leverage-up"); break;
        case "-": emit("leverage-down"); break;
        default: break;
      }
    },
    [onOpenHelp]
  );

  useEffect(() => {
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handler]);
}
